import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { Footer } from "@/components/Footer";

const serviceUrl = "https://smilecaredentalgandhinagar.lovable.app/services/dental-implants-cost-gandhinagar";
const imageUrl = "https://smilecaredentalgandhinagar.lovable.app/hero-dental.webp";

const costSchema = {
  "@context": "https://schema.org",
  "@type": "MedicalProcedure",
  name: "Dental Implants in Gandhinagar — Cost Guide",
  alternateName: "Dental Implant Cost Gandhinagar",
  description:
    "Dental implant cost guide for Gandhinagar patients at Smile Care Dental — implant types, what affects price, and value vs dentures.",
  bodyLocation: "Jawbone, missing tooth area",
  procedureType: "SurgicalProcedure",
  image: imageUrl,
  url: serviceUrl,
  provider: {
    "@type": "Dentist",
    name: "Smile Care Dental",
    url: "https://smilecaredentalgandhinagar.lovable.app/",
    telephone: "+916353963147",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Pramukh Arcade 2, A-212, near Terracotta, opposite NID college",
      addressLocality: "Kudasan",
      addressRegion: "Gujarat",
      postalCode: "382421",
      addressCountry: "IN",
    },
  },
  areaServed: [
    { "@type": "City", name: "Gandhinagar" },
    { "@type": "Place", name: "Kudasan" },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How much do dental implants cost in Gandhinagar?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Single tooth dental implants in Gandhinagar typically range from ₹18,000 to ₹55,000 per tooth depending on implant brand, bone condition, and the crown material. A final estimate is shared at Smile Care Dental after a clinical exam and digital X-ray.",
      },
    },
    {
      "@type": "Question",
      name: "What affects the price of a dental implant?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Implant brand and origin, crown material (zirconia or PFM), need for bone grafting or sinus lift, number of implants, and use of guided digital planning are the main factors that influence dental implant cost.",
      },
    },
    {
      "@type": "Question",
      name: "Are dental implants better value than dentures?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Implants cost more upfront but last decades, feel like natural teeth, preserve jawbone, and do not need adhesives. Dentures are cheaper initially but are typically replaced every 5–7 years and can affect chewing comfort.",
      },
    },
  ],
};

const factors = [
  "Implant brand and country of origin (Indian, Korean, Swiss, German systems)",
  "Crown material — zirconia, PFM, or full-ceramic",
  "Need for bone grafting, sinus lift, or extraction before placement",
  "Number of implants and whether All-on-4 / All-on-6 is required",
  "Digital guided surgery and 3D CBCT planning",
];

const implantTypes = [
  {
    name: "Standard Single Tooth Implant",
    range: "₹18,000 – ₹35,000",
    note: "Most common option for one missing tooth — Indian or Korean implant systems.",
  },
  {
    name: "Premium Implant (Swiss / German)",
    range: "₹38,000 – ₹55,000",
    note: "Long-term warranty systems for patients wanting top-tier longevity.",
  },
  {
    name: "All-on-4 / All-on-6 Full Arch",
    range: "₹1,80,000 – ₹4,50,000 per arch",
    note: "Fixed full-mouth rehabilitation for fully edentulous patients.",
  },
];

const implantsVsDentures = [
  "Implants preserve jawbone; dentures accelerate bone loss",
  "Implants feel like natural teeth; dentures can shift while chewing",
  "Implants last 15–25+ years; dentures usually need replacement every 5–7 years",
  "No adhesives, no nightly removal, and natural speech with implants",
];

const DentalImplantsCost = () => {
  return (
    <>
      <Helmet>
        <title>Dental Implants Cost in Gandhinagar | Smile Care Dental</title>
        <meta
          name="description"
          content="Dental implants cost in Gandhinagar — transparent price ranges, implant types, factors affecting cost, and implants vs dentures value comparison by Smile Care Dental, Kudasan."
        />
        <meta
          name="keywords"
          content="dental implants cost gandhinagar, dental implant price kudasan, single tooth implant cost gandhinagar, all on 4 cost gujarat, dentures vs implants gandhinagar"
        />
        <link rel="canonical" href={serviceUrl} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Dental Implants Cost in Gandhinagar — Full Price Guide" />
        <meta
          property="og:description"
          content="Transparent dental implant pricing in Gandhinagar, types of implants, and value comparison vs dentures by MDS specialists at Smile Care Dental."
        />
        <meta property="og:url" content={serviceUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Dental Implants Cost in Gandhinagar | Smile Care Dental" />
        <meta
          name="twitter:description"
          content="How much do dental implants cost in Gandhinagar? See price ranges, implant types, and value vs dentures."
        />
        <meta name="twitter:image" content={imageUrl} />
        <script type="application/ld+json">{JSON.stringify(costSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header />
      <main>
        <section className="pt-32 pb-16 bg-background">
          <div className="container mx-auto px-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to home
            </Link>

            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center">
              <div>
                <span className="inline-block bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-5">
                  Transparent Pricing
                </span>
                <h1 className="text-4xl md:text-5xl font-bold text-foreground leading-tight mb-6">
                  Dental Implants Cost in Gandhinagar
                </h1>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl">
                  Wondering how much dental implants cost in Gandhinagar? At Smile Care Dental in Kudasan, our MDS specialists offer single tooth implants, premium global systems, and full-arch All-on-4/6 rehabilitation with clear, upfront pricing — no surprise add-ons after treatment begins.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button variant="hero" size="xl" asChild>
                    <a href="tel:+916353963147">
                      <Phone className="w-5 h-5" />
                      Call 063539 63147
                    </a>
                  </Button>
                  <Button variant="whatsapp" size="xl" asChild>
                    <a
                      href="https://wa.me/916353963147?text=Hi%2C%20I%20want%20a%20dental%20implant%20cost%20estimate%20in%20Gandhinagar"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MessageCircle className="w-5 h-5" />
                      Get a Cost Estimate
                    </a>
                  </Button>
                </div>
              </div>

              <img
                src="/hero-dental.webp"
                alt="Smile Care Dental clinic for dental implants in Gandhinagar"
                width={888}
                height={500}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full aspect-[4/3] object-cover rounded-2xl shadow-card"
              />
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8">
              Dental implant price ranges in Gandhinagar
            </h2>
            <div className="grid gap-5 md:grid-cols-3">
              {implantTypes.map((t) => (
                <div key={t.name} className="bg-card border border-border/50 rounded-2xl p-6 shadow-soft">
                  <h3 className="text-lg font-semibold text-foreground mb-2">{t.name}</h3>
                  <p className="text-2xl font-bold text-primary mb-3">{t.range}</p>
                  <p className="text-muted-foreground text-sm">{t.note}</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-6">
              Indicative prices for the Gandhinagar market. Your final dental implant cost is confirmed only after a clinical exam, digital X-ray, and bone assessment at our Kudasan clinic.
            </p>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-10 max-w-6xl">
            <article className="bg-card border border-border/50 rounded-2xl p-6 md:p-8 shadow-soft">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                What affects dental implant cost?
              </h2>
              <ul className="space-y-4">
                {factors.map((f) => (
                  <li key={f} className="flex gap-3 text-muted-foreground">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </article>

            <article className="bg-card border border-border/50 rounded-2xl p-6 md:p-8 shadow-soft">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                Dental implants vs dentures — value comparison
              </h2>
              <ul className="space-y-4">
                {implantsVsDentures.map((f) => (
                  <li key={f} className="flex gap-3 text-muted-foreground">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </article>
          </div>
        </section>

        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Get a personalised implant quote today
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Book a consultation at Smile Care Dental, Kudasan for a precise dental implant cost estimate and a treatment plan tailored to your bone health and lifestyle.
            </p>
            <Button variant="hero" size="xl" asChild>
              <a href="tel:+916353963147">
                <Phone className="w-5 h-5" />
                Call for Appointment
              </a>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </>
  );
};

export default DentalImplantsCost;
